import React, { useState } from 'react';
import '../css/Sidebar.css';
import { Link } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Drawer,
  IconButton,
  Divider,
  Collapse
} from '@mui/material';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import addressIcon from '../icons/address.png';
import homeIcon from '../icons/home.png';
import uploadIcon from '../icons/upload.png';
import locationIcon from '../icons/location.png';
import countryIcon from '../icons/countries.png';
import stateIcon from '../icons/states.png';
import staticIcon from '../icons/staticData.png';
import cityIcon from '../icons/cities.png';
import unlocodeIcon from '../icons/unlocodes.png';
import trackingIcon from '../icons/tracking.png';
import jobTrackingIcon from '../icons/jobTracking.png';
import zoneIcon from '../icons/zone.png';
import { _routes } from '../utils/Routes';
import { useTranslation } from 'react-i18next';

const icons = {
  Home: <img src={homeIcon} alt="Home" style={{ width: 25, height: 25, margin: 10 }} />,
  Locations: <img src={locationIcon} alt="Locations" style={{ width: 25, height: 25, margin: 10 }} />,
  Zones: <img src={zoneIcon} alt="Zones" style={{ width: 25, height: 25, margin: 10 }} />,
  Addresses: <img src={addressIcon} alt="Addresses" style={{ width: 25, height: 25, margin: 10 }} />,
  Upload: <img src={uploadIcon} alt="Upload" style={{ width: 25, height: 25, margin: 10 }} />,
  "Upload Location": <img src={locationIcon} alt="Upload Location" style={{ width: 25, height: 25, margin: 10 }} />,
  "Bulk Upload": <img src={uploadIcon} alt="Bulk Upload" style={{ width: 25, height: 25, margin: 10 }} />,
  "Bulk Upload (New)": <img src={uploadIcon} alt="Bulk Upload (New)" style={{ width: 25, height: 25, margin: 10 }} />,
  Countries: <img src={countryIcon} alt="Country" style={{ width: 25, height: 25, margin: 10 }} />,
  States: <img src={stateIcon} alt="States" style={{ width: 25, height: 25, margin: 10 }} />,
  Cities: <img src={cityIcon} alt="Cities" style={{ width: 25, height: 25, margin: 10 }} />,
  Unlocodes: <img src={unlocodeIcon} alt="Unlocodes" style={{ width: 25, height: 25, margin: 10 }} />,
  staticData: <img src={staticIcon} alt="Upload" style={{ width: 25, height: 25, margin: 10 }} />,
  Tracking: <img src={trackingIcon} alt="Tracking" style={{ width: 25, height: 25, margin: 10 }} />,
  'Job Tracking': <img src={jobTrackingIcon} alt="Tracking" style={{ width: 25, height: 25, margin: 10 }} />,
};

const drawerWidth = 280;
const sidebarBg = theme => '#123456';

// Reusable navigation item
const NavItem = ({ route, open, t, nested = false }) => (
  <ListItem
    button
    key={route.title}
    sx={{
      pl: nested ? 5 : 2,
      color: '#fff',
      '& .MuiListItemIcon-root': { color: '#fff' },
      '& .MuiListItemText-primary': { color: '#fff' },
    }}
  >
    <Link to={route.route} style={{ display: 'contents', textDecoration: 'none', color: 'white' }}>
      <ListItemIcon> {icons[route.title] || icons.Home} </ListItemIcon>
      {open && <ListItemText primary={t(route.title)} />}
    </Link>
  </ListItem>
);

// Reusable navigation group (for dropdowns)
const NavGroup = ({ title, icon, children, open, expanded, onClick }) => (
  <>
    <ListItem
      button
      onClick={onClick}
      sx={{
        color: '#fff',
        '& .MuiListItemIcon-root': { color: '#fff' },
        '& .MuiListItemText-primary': { color: '#fff' },
      }}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      {open && <ListItemText primary={title} />}
      {open && (expanded ? <ExpandLess /> : <ExpandMore />)}
    </ListItem>
    <Collapse in={expanded && open} timeout="auto" unmountOnExit>
      <List component="div" disablePadding>
        {children}
      </List>
    </Collapse>
  </>
);

const Sidebar = () => {
  const [routes] = useState(_routes);
  const [t] = useTranslation();
  const [open, setOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ Home: false, Upload: false });

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);
  const handleGroupClick = (group) => setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));

  // Define navigation structure
  const navStructure = [
    {
      group: 'Home',
      icon: icons.Home,
      children: ['Locations', 'Addresses', 'Zones']
    },
    {
      group: 'Upload',
      icon: icons.Upload,
      children: ['Upload Location', 'Bulk Upload', 'Bulk Upload (New)']
    },
    {
      group: 'Tracking',
      icon: icons.Tracking, // Replace with a tracking-specific icon if available
      children: ['Job Tracking']
    },
    {
      group: 'Static Data',
      icon: icons.staticData,
      children: ['Countries', 'States', 'Cities', 'Unlocodes']
    }
    // Add more groups here if needed
  ];

  // Helper to get route by title
  const getRoute = (title) => routes.find(route => route.title === title);

  return (
    <div style={{ display: 'flex' }}>
      <Drawer
        onMouseEnter={handleDrawerOpen}
        onMouseLeave={handleDrawerClose}
        variant="permanent"
        sx={theme => ({
          width: open ? drawerWidth : 80,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: open ? drawerWidth : 80,
            background: sidebarBg(theme),
            color: '#fff',
            boxSizing: 'border-box',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
            }),
            overflowX: 'hidden',
          },
        })}
        open={open}
      >
        <div style={{ minHeight: 64 }}>
          <IconButton sx={{ color: '#fff' }} onClick={handleDrawerClose}>
            <ChevronRightIcon />
          </IconButton>
        </div>
        <Divider sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />
        <List>
          {/* Render navigation groups */}
          {navStructure.map(group => (
            <NavGroup
              key={group.group}
              title={t(group.group)}
              icon={group.icon}
              open={open}
              expanded={!!expandedGroups[group.group]}
              onClick={() => handleGroupClick(group.group)}
            >
              {group.children.map(childTitle => {
                const route = getRoute(childTitle);
                return route ? (
                  <NavItem
                    key={route.title}
                    route={route}
                    open={open}
                    t={t}
                    nested
                  />
                ) : null;
              })}
            </NavGroup>
          ))}
        </List>
      </Drawer>
    </div>
  );
};

export default Sidebar;
